import React, { useState, useEffect, useCallback } from 'react';
import { EyeIcon, EyeSlashIcon, CheckCircleIcon, XCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import AuthBackground from '../components/AuthBackground';

// Types
interface PasswordStrength {
  valid: boolean;
  score: number;
  maxScore: number;
  strength: 'weak' | 'fair' | 'good' | 'strong';
  requirements: string[];
}

interface UsernameCheckResult {
  available: boolean;
  message: string;
}

interface SignupResponse {
  message: string;
  userId: number;
  email: string;
  requiresVerification: boolean;
  verificationMethod: string;
  phoneOtpSent: boolean;
  phoneNumber?: string;
}

type SignupStep = 'form' | 'email-verification' | 'phone-verification' | 'success';

const inputClass = "appearance-none block w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[color:var(--focus-ring)] focus:border-transparent focus:bg-white transition-all duration-200";
const inputClassValid = "appearance-none block w-full px-4 py-2.5 bg-gray-50 border border-green-400 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[color:var(--focus-ring)] focus:border-transparent focus:bg-white transition-all duration-200";
const inputClassError = "appearance-none block w-full px-4 py-2.5 bg-gray-50 border border-red-400 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[color:var(--focus-ring)] focus:border-transparent focus:bg-white transition-all duration-200";
const inputClassWarn = "appearance-none block w-full px-4 py-2.5 bg-gray-50 border border-yellow-400 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[color:var(--focus-ring)] focus:border-transparent focus:bg-white transition-all duration-200";

const Signup: React.FC = () => {
  const navigate = useNavigate();

  // Form state
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [country, setCountry] = useState('IN');
  const [showPassword, setShowPassword] = useState(false);

  // Step management
  const [currentStep, setCurrentStep] = useState<SignupStep>('form');
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [registeredPhone, setRegisteredPhone] = useState('');

  // OTP states
  const [emailOtp, setEmailOtp] = useState(['', '', '', '', '', '']);
  const [phoneOtp, setPhoneOtp] = useState(['', '', '', '', '', '']);

  // Validation states
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'available' | 'taken' | 'invalid'>('idle');
  const [usernameMessage, setUsernameMessage] = useState('');
  const [emailStatus, setEmailStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const [emailMessage, setEmailMessage] = useState('');
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength | null>(null);

  // UI states
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const usernameTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const emailTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const checkUsername = useCallback(async (value: string) => {
    if (!value || value.length < 3) { setUsernameStatus('idle'); setUsernameMessage(''); return; }
    setUsernameStatus('checking');
    try {
      const response = await api.get(`/auth/check-username?username=${encodeURIComponent(value)}`);
      const data = response.data as UsernameCheckResult;
      setUsernameStatus(data.available ? 'available' : 'taken');
      setUsernameMessage(data.message);
    } catch { setUsernameStatus('invalid'); setUsernameMessage('Unable to check username'); }
  }, []);

  const checkEmail = useCallback(async (value: string) => {
    if (!value || !value.includes('@')) { setEmailStatus('idle'); setEmailMessage(''); return; }
    setEmailStatus('checking');
    try {
      const response = await api.get(`/auth/check-email?email=${encodeURIComponent(value)}`);
      const data = response.data as { available: boolean; message: string };
      setEmailStatus(data.available ? 'available' : 'taken');
      setEmailMessage(data.message);
    } catch { setEmailStatus('idle'); setEmailMessage(''); }
  }, []);

  const validatePassword = useCallback((value: string) => {
    if (!value) { setPasswordStrength(null); return; }
    const requirements: string[] = [];
    let score = 0;
    const hasMinLength = value.length >= 8; if (hasMinLength) score++; requirements.push(`At least 8 characters:${hasMinLength ? 'met' : 'unmet'}`);
    const hasUppercase = /[A-Z]/.test(value); if (hasUppercase) score++; requirements.push(`At least one uppercase letter:${hasUppercase ? 'met' : 'unmet'}`);
    const hasLowercase = /[a-z]/.test(value); if (hasLowercase) score++; requirements.push(`At least one lowercase letter:${hasLowercase ? 'met' : 'unmet'}`);
    const hasDigit = /\d/.test(value); if (hasDigit) score++; requirements.push(`At least one number:${hasDigit ? 'met' : 'unmet'}`);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>[\]\-_+=;'`~\\/]/.test(value); if (hasSpecial) score++; requirements.push(`At least one special character:${hasSpecial ? 'met' : 'unmet'}`);
    if (value.length >= 12) score++;
    if (value.length >= 16) score++;
    let strength: 'weak' | 'fair' | 'good' | 'strong';
    if (score <= 2) strength = 'weak'; else if (score <= 4) strength = 'fair'; else if (score <= 5) strength = 'good'; else strength = 'strong';
    setPasswordStrength({ valid: hasMinLength && hasUppercase && hasLowercase && hasDigit && hasSpecial, score, maxScore: 7, strength, requirements });
  }, []);

  useEffect(() => { if (usernameTimeoutRef.current) clearTimeout(usernameTimeoutRef.current); usernameTimeoutRef.current = setTimeout(() => checkUsername(username), 500); return () => { if (usernameTimeoutRef.current) clearTimeout(usernameTimeoutRef.current); }; }, [username, checkUsername]);
  useEffect(() => { if (emailTimeoutRef.current) clearTimeout(emailTimeoutRef.current); emailTimeoutRef.current = setTimeout(() => checkEmail(email), 500); return () => { if (emailTimeoutRef.current) clearTimeout(emailTimeoutRef.current); }; }, [email, checkEmail]);
  useEffect(() => { validatePassword(password); }, [password, validatePassword]);
  useEffect(() => { if (resendCooldown > 0) { const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000); return () => clearTimeout(timer); } }, [resendCooldown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (usernameStatus !== 'available') { setError('Please choose a valid available username'); return; }
    if (emailStatus !== 'available') { setError('Please use a valid available email address'); return; }
    if (!passwordStrength?.valid) { setError('Please create a stronger password'); return; }
    setLoading(true);
    try {
      const response = await api.post('/auth/signup', { username: username.trim(), email: email.trim().toLowerCase(), password, phoneNumber: phoneNumber.trim() || undefined, verificationMethod: 'otp', country });
      const data = response.data as SignupResponse;
      setRegisteredEmail(data.email);
      if (data.phoneOtpSent && data.phoneNumber) setRegisteredPhone(data.phoneNumber);
      setCurrentStep('email-verification');
      setResendCooldown(60);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      setError(error.response?.data?.message || error.message || 'Signup failed. Please try again.');
    } finally { setLoading(false); }
  };

  const handleOtpChange = (index: number, value: string, otp: string[], setOtp: React.Dispatch<React.SetStateAction<string[]>>) => {
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newOtp = [...otp];
      digits.forEach((digit, i) => { if (index + i < 6) newOtp[index + i] = digit; });
      setOtp(newOtp);
      const nextIndex = Math.min(index + digits.length, 5);
      document.getElementById(`otp-${otp === emailOtp ? 'email' : 'phone'}-${nextIndex}`)?.focus();
      return;
    }
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp]; newOtp[index] = value; setOtp(newOtp);
    if (value && index < 5) { const type = otp === emailOtp ? 'email' : 'phone'; document.getElementById(`otp-${type}-${index + 1}`)?.focus(); }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent, otp: string[], type: 'email' | 'phone') => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) document.getElementById(`otp-${type}-${index - 1}`)?.focus();
  };

  const verifyEmailOtp = async () => {
    const otpString = emailOtp.join('');
    if (otpString.length !== 6) { setError('Please enter the complete 6-digit OTP'); return; }
    setLoading(true); setError(null);
    try {
      await api.post('/auth/verify-email/otp', { email: registeredEmail, otp: otpString });
      if (registeredPhone) { setCurrentStep('phone-verification'); setResendCooldown(60); } else setCurrentStep('success');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      setError(error.response?.data?.message || 'Invalid OTP. Please try again.');
    } finally { setLoading(false); }
  };

  const verifyPhoneOtp = async () => {
    const otpString = phoneOtp.join('');
    if (otpString.length !== 6) { setError('Please enter the complete 6-digit OTP'); return; }
    setLoading(true); setError(null);
    try {
      await api.post('/auth/verify-phone/otp', { phoneNumber: registeredPhone, otp: otpString });
      setCurrentStep('success');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      setError(error.response?.data?.message || 'Invalid OTP. Please try again.');
    } finally { setLoading(false); }
  };

  const resendOtp = async (type: 'email' | 'phone') => {
    if (resendCooldown > 0) return;
    setLoading(true); setError(null);
    try {
      if (type === 'email') await api.post('/auth/resend-verification', { email: registeredEmail, method: 'otp' });
      else await api.post('/auth/resend-phone-verification', { email: registeredEmail });
      setResendCooldown(60);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      setError(error.response?.data?.message || 'Failed to resend OTP. Please try again.');
    } finally { setLoading(false); }
  };

  const renderOtpInput = (otp: string[], setOtp: React.Dispatch<React.SetStateAction<string[]>>, type: 'email' | 'phone') => (
    <div className="flex justify-center gap-2">
      {otp.map((digit, index) => (
        <input
          key={index}
          id={`otp-${type}-${index}`}
          type="text"
          inputMode="numeric"
          maxLength={6}
          value={digit}
          onChange={(e) => handleOtpChange(index, e.target.value, otp, setOtp)}
          onKeyDown={(e) => handleOtpKeyDown(index, e, otp, type)}
          className="w-11 h-13 text-center text-xl font-semibold bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[color:var(--focus-ring)] focus:border-transparent focus:bg-white transition-all"
          disabled={loading}
        />
      ))}
    </div>
  );

  const renderPasswordStrength = () => {
    if (!passwordStrength) return null;
    const colors = { weak: 'bg-red-500', fair: 'bg-yellow-500', good: 'bg-blue-500', strong: 'bg-green-500' };
    const widths = { weak: 'w-1/4', fair: 'w-2/4', good: 'w-3/4', strong: 'w-full' };
    const textColors = { weak: 'text-red-500', fair: 'text-yellow-500', good: 'text-blue-500', strong: 'text-green-500' };
    return (
      <div className="mt-2 space-y-2">
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div className={`h-full ${colors[passwordStrength.strength]} ${widths[passwordStrength.strength]} transition-all duration-300`} />
          </div>
          <span className={`text-xs font-medium capitalize ${textColors[passwordStrength.strength]}`}>{passwordStrength.strength}</span>
        </div>
        <ul className="space-y-0.5">
          {passwordStrength.requirements.map((req, index) => {
            const [text, status] = req.split(':');
            const isMet = status === 'met';
            return (
              <li key={index} className={`flex items-center gap-1.5 text-xs ${isMet ? 'text-green-600' : 'text-gray-400'}`}>
                {isMet ? <CheckIcon className="w-3.5 h-3.5" /> : <XCircleIcon className="w-3.5 h-3.5" />}
                {text}
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  const renderStatusIndicator = (status: string, message: string) => {
    if (status === 'idle') return null;
    return (
      <div className={`flex items-center gap-1.5 mt-1 text-xs ${status === 'checking' ? 'text-gray-500' : status === 'available' ? 'text-green-600' : 'text-red-600'}`}>
        {status === 'checking' ? <div className="w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" /> : status === 'available' ? <CheckCircleIcon className="w-4 h-4" /> : <ExclamationCircleIcon className="w-4 h-4" />}
        {message}
      </div>
    );
  };

  // Colour in classes, not the style object — an inline background outranks
  // Tailwind's disabled: variants, so opacity-50 faded fill and label together
  // and the CTA read as blank. Create Account starts disabled, so that state
  // has to stay legible.
  const gradientBtnClass = "w-full py-3 px-4 rounded-xl font-semibold text-sm text-center flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--focus-ring)] bg-[color:var(--action)] text-[color:var(--action-text)] hover:bg-[color:var(--action-hover)] disabled:bg-[color:var(--bg-sunken)] disabled:text-[color:var(--text-disabled)] disabled:cursor-not-allowed";
  // A function, not a const: the shadow has to clear when the button is
  // disabled, and an inline style outranks any `disabled:` class — so the
  // disabled state must be passed in rather than expressed in CSS. A saffron
  // glow around a grey fill reads as a rendering fault.
  const gradientBtnStyle = (isDisabled = false) => ({
    boxShadow: isDisabled ? 'none' : '0 1px 3px color-mix(in srgb, var(--action) 30%, transparent)',
  });

  const renderContent = () => {
    if (currentStep === 'success') {
      return (
        <div className="px-8 pb-6 pt-4 text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircleIcon className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Account created!</h2>
          <p className="text-sm text-gray-500 mb-6">Your account has been successfully created and verified.</p>
          <button onClick={() => navigate('/login')} className={gradientBtnClass} style={gradientBtnStyle()}>
            Sign In Now
          </button>
        </div>
      );
    }

    if (currentStep === 'email-verification') {
      return (
        <div className="px-8 pb-6 pt-4 text-center">
          <div className="mx-auto w-14 h-14 bg-[color:var(--status-info-bg)] rounded-full flex items-center justify-center mb-4">
            <svg className="w-7 h-7 text-[color:var(--status-info-text)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">Verify your email</h2>
          <p className="text-sm text-gray-500 mb-1">We've sent a 6-digit code to</p>
          <p className="text-sm font-semibold text-primary mb-5">{registeredEmail}</p>
          <div className="mb-5">{renderOtpInput(emailOtp, setEmailOtp, 'email')}</div>
          {error && <div role="alert" className="mb-4 p-3 text-sm bg-red-50 border border-red-200 text-red-700 rounded-xl">{error}</div>}
          <button onClick={verifyEmailOtp} disabled={loading || emailOtp.join('').length !== 6} className={gradientBtnClass} style={gradientBtnStyle(loading || emailOtp.join('').length !== 6)}>
            {loading ? 'Verifying...' : 'Verify Email'}
          </button>
          <p className="text-sm text-gray-500 mt-4">
            Didn't receive the code?{' '}
            <button onClick={() => resendOtp('email')} disabled={resendCooldown > 0 || loading} className="font-semibold text-primary hover:underline disabled:opacity-50 transition-colors">
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend'}
            </button>
          </p>
        </div>
      );
    }

    if (currentStep === 'phone-verification') {
      return (
        <div className="px-8 pb-6 pt-4 text-center">
          <div className="mx-auto w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-7 h-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">Verify your phone</h2>
          <p className="text-sm text-gray-500 mb-1">We've sent a 6-digit code to</p>
          <p className="text-sm font-semibold text-primary mb-5">{registeredPhone}</p>
          <div className="mb-5">{renderOtpInput(phoneOtp, setPhoneOtp, 'phone')}</div>
          {error && <div role="alert" className="mb-4 p-3 text-sm bg-red-50 border border-red-200 text-red-700 rounded-xl">{error}</div>}
          <button onClick={verifyPhoneOtp} disabled={loading || phoneOtp.join('').length !== 6} className={gradientBtnClass} style={gradientBtnStyle(loading || phoneOtp.join('').length !== 6)}>
            {loading ? 'Verifying...' : 'Verify Phone'}
          </button>
          <button onClick={() => setCurrentStep('success')} disabled={loading} className="w-full py-2 px-4 text-sm text-gray-500 hover:text-gray-700 transition-colors mt-2">
            Skip for now
          </button>
          <p className="text-sm text-gray-500 mt-3">
            Didn't receive the code?{' '}
            <button onClick={() => resendOtp('phone')} disabled={resendCooldown > 0 || loading} className="font-semibold text-primary hover:underline disabled:opacity-50 transition-colors">
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend'}
            </button>
          </p>
        </div>
      );
    }

    // Registration form
    return (
      <div className="px-8 pb-6 pt-5">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Username */}
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1.5">Username</label>
            <input id="username" type="text" autoComplete="username" required placeholder="Choose a username" value={username} onChange={(e) => setUsername(e.target.value)} disabled={loading}
              className={usernameStatus === 'available' ? inputClassValid : (usernameStatus === 'taken' || usernameStatus === 'invalid') ? inputClassError : inputClass} />
            {renderStatusIndicator(usernameStatus, usernameMessage)}
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">Email address</label>
            <input id="email" type="email" autoComplete="email" required placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={loading}
              className={emailStatus === 'available' ? inputClassValid : emailStatus === 'taken' ? inputClassError : inputClass} />
            {renderStatusIndicator(emailStatus, emailMessage)}
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
            <div className="relative">
              <input id="password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" required placeholder="Create a strong password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={loading}
                className={`pr-12 ${passwordStrength?.valid ? inputClassValid : (password && !passwordStrength?.valid) ? inputClassWarn : inputClass}`} />
              <button type="button" onClick={() => setShowPassword(v => !v)} className="absolute inset-y-0 right-0 px-3.5 flex items-center text-gray-500 hover:text-gray-700 transition-colors" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
              </button>
            </div>
            {renderPasswordStrength()}
          </div>

          {/* Country */}
          <div>
            <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1.5">
              Country
            </label>
            <select id="country" value={country} onChange={(e) => setCountry(e.target.value)} disabled={loading} className={inputClass}>
              <option value="IN">India (₹ INR)</option>
              <option value="GB">United Kingdom (£ GBP)</option>
            </select>
            <p className="mt-1 text-xs text-gray-400">Sets your currency, tax rules and timezone</p>
          </div>

          {/* Phone */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1.5">
              Phone Number <span className="text-gray-400 text-xs">(Optional)</span>
            </label>
            <input id="phone" type="tel" autoComplete="tel" placeholder={country === 'GB' ? '+44 7XXX XXXXXX' : '+91 XXXXX XXXXX'} value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} disabled={loading} className={inputClass} />
            <p className="mt-1 text-xs text-gray-400">Receive order updates via SMS</p>
          </div>

          {error && (
            <div role="alert" className="flex items-start gap-2 p-3 text-sm bg-red-50 border border-red-200 text-red-700 rounded-xl">
              <svg className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd"/>
              </svg>
              <span>{error}</span>
            </div>
          )}

          <button type="submit" disabled={loading || usernameStatus !== 'available' || emailStatus !== 'available' || !passwordStrength?.valid} className={gradientBtnClass} style={gradientBtnStyle(loading || usernameStatus !== 'available' || emailStatus !== 'available' || !passwordStrength?.valid)}>
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                Creating Account...
              </span>
            ) : 'Create Account'}
          </button>

          <p className="text-center text-sm text-gray-500 mt-2">
            Already have an account?{' '}
            <a href="/login" className="font-semibold text-primary hover:underline transition-colors">Sign in</a>
          </p>
        </form>
      </div>
    );
  };

  const getHeaderContent = () => {
    if (currentStep === 'form') {
      return (
        <div className="pt-4 pb-1 px-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4" style={{ background: 'var(--action)', boxShadow: '0 1px 3px color-mix(in srgb, var(--action) 30%, transparent)' }}>
            <span className="text-white font-bold text-lg tracking-wider">SOG</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Create an account</h1>
          <p className="text-sm text-gray-500 mt-1">Get started with SOG today</p>
        </div>
      );
    }
    return null; // Other steps have headers built into renderContent
  };

  return (
    <AuthBackground>
      {/* Card */}
      <div className="relative w-full max-w-[420px] mx-4 my-8">
        <div className="rounded-2xl" style={{ backgroundColor: 'rgba(255, 255, 255, 0.97)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', boxShadow: '0 25px 60px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)' }}>

          {/* Back button */}
          <div className="px-8 pt-6">
            <button
              onClick={() => currentStep === 'form' ? navigate('/login') : setCurrentStep('form')}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary transition-colors"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              {currentStep === 'form' ? 'Back to login' : 'Back'}
            </button>
          </div>

          {getHeaderContent()}
          {renderContent()}

          {/* Footer */}
          <div className="px-8 pb-6">
            <div className="flex items-center justify-center gap-1.5 text-xs text-gray-400">
              <span>Powered by</span>
              <img src="/favicon-64.png" alt="Nextserve" className="h-4 w-4 rounded-sm" />
              <span className="font-medium text-gray-500">Nextserve</span>
            </div>
          </div>
        </div>
      </div>
    </AuthBackground>
  );
};

export default Signup;
